import React, { useEffect } from 'react';
import { BUFFER_SIZE } from './types';
import type { LayerVisibility } from './types';

interface GridCanvasInput {
    canvasRef: React.RefObject<HTMLCanvasElement | null>;
    dimensions: { width: number; height: number };
    zoomRef: React.MutableRefObject<number>;
    panRef: React.MutableRefObject<{ x: number; y: number }>;
    isCustomUpload: boolean;
    customMapImageRef?: React.RefObject<HTMLImageElement | null>;
    cameraTick: number;
    layerVisibility: LayerVisibility;
}

export function useGridCanvas({
    canvasRef,
    dimensions,
    zoomRef,
    panRef,
    isCustomUpload,
    customMapImageRef,
    cameraTick,
    layerVisibility,
}: GridCanvasInput): void {
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const { width, height } = dimensions;
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, width, height);

        if (!layerVisibility.grid) return;

        const isCustom = isCustomUpload && !!customMapImageRef?.current;
        const mW = isCustom ? customMapImageRef!.current!.naturalWidth : BUFFER_SIZE;
        const mH = isCustom ? customMapImageRef!.current!.naturalHeight : BUFFER_SIZE;

        let rW = Math.min(width, height);
        let rH = Math.min(width, height);
        if (isCustom) {
            const ratio = mW / mH;
            if (width / height > ratio) { rH = height; rW = height * ratio; }
            else { rW = width; rH = width / ratio; }
        }

        const zoom = zoomRef.current;
        const pan = panRef.current;

        ctx.save();
        ctx.translate(pan.x, pan.y);
        ctx.scale((rW / mW) * zoom, (rH / mH) * zoom);
        
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
        ctx.lineWidth = 1 / ((rW / mW) * zoom); // 1px on screen
        ctx.beginPath();

        const gridSize = layerVisibility.gridSize || 50;

        if (layerVisibility.gridType === 'square') {
            for (let x = 0; x <= mW; x += gridSize) {
                ctx.moveTo(x, 0);
                ctx.lineTo(x, mH);
            }
            for (let y = 0; y <= mH; y += gridSize) {
                ctx.moveTo(0, y);
                ctx.lineTo(mW, y);
            }
        } else if (layerVisibility.gridType === 'hex') {
            // Hex grid math (pointy-topped)
            const h = gridSize;
            const w = Math.sqrt(3) * h / 2;
            const ySpacing = h * 3/4;
            
            for (let y = 0, row = 0; y <= mH + h; y += ySpacing, row++) {
                const offset = (row % 2 === 1) ? w/2 : 0;
                for (let x = 0; x <= mW + w; x += w) {
                    const cx = x + offset;
                    const cy = y;
                    
                    if (cx > mW + w || cy > mH + h) continue;

                    // Draw one hex (or just its bottom-right edges to form a grid without overlap)
                    // We'll draw the full hex for simplicity
                    ctx.moveTo(cx, cy - h/2);
                    ctx.lineTo(cx + w/2, cy - h/4);
                    ctx.lineTo(cx + w/2, cy + h/4);
                    ctx.lineTo(cx, cy + h/2);
                    ctx.lineTo(cx - w/2, cy + h/4);
                    ctx.lineTo(cx - w/2, cy - h/4);
                    ctx.lineTo(cx, cy - h/2);
                }
            }
        }
        
        ctx.stroke();
        ctx.restore();
    }, [
        canvasRef, dimensions, zoomRef, panRef, isCustomUpload,
        customMapImageRef, cameraTick, layerVisibility.grid,
        layerVisibility.gridType, layerVisibility.gridSize
    ]);
}
